import React, { useEffect } from 'react';
import styled from 'styled-components';
import TrueImpactDollarField from 'shared/design-system/components/TrueImpactDollarField';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { AnswerInput } from 'shared/providers/ProgramProvider';
import RelevancyCheckbox, {
  defaultCostValue,
} from 'npo/pages/ReportBuilder/Components/Budget/RelevancyCheckbox';
import {
  DIRECT_COST_QUESTION_ID,
  IN_KIND_DONATIONS_QUESTION_ID,
  OVERHEAD_COST_QUESTION_ID,
  getBudgetDollarAmount,
} from 'npo/pages/ReportBuilder/Components/Budget/utils';
import { directCostCheckboxText } from 'npo/pages/ReportBuilder/Components/Budget/ProgramCost';
import { inKindDonationsCheckboxText } from 'npo/pages/ReportBuilder/Components/Budget/BudgetDonations';
import { overheadCostCheckboxText } from 'npo/pages/ReportBuilder/Components/Budget/BudgetOverheadCost';
import BudgetIntro from 'npo/pages/ReportBuilder/Components/Budget/BudgetIntro';
import { GuidanceTextResponse } from 'npo/pages/ReportBuilder/Components/utils';

const emptyGuidance: GuidanceTextResponse = {};
const noop = () => undefined;

export const BUDGET_INTRO_QUESTION_ID = 80001;
const BUDGET_INTRO_ANSWER = 'Yes, prepare budget';

export const budgetCurrencyQuestionIds: ReadonlySet<number> = new Set([
  DIRECT_COST_QUESTION_ID,
  IN_KIND_DONATIONS_QUESTION_ID,
  OVERHEAD_COST_QUESTION_ID,
]);

export const budgetQuestionIds: ReadonlySet<number> = new Set([
  BUDGET_INTRO_QUESTION_ID,
  DIRECT_COST_QUESTION_ID,
  IN_KIND_DONATIONS_QUESTION_ID,
  OVERHEAD_COST_QUESTION_ID,
]);

const BudgetIntroField: React.FC<{
  value: AnswerInput;
  onChange: (next: AnswerInput) => void;
}> = ({ value, onChange }) => {
  // Seed a sentinel answer so isAnswerFilled() is true, letting
  // handleSaveAndAdvance persist the answer and mark the intro complete.
  // Depend on `value` (object identity) so we re-seed whenever
  // ProgramQuestionsModal's foundAnswer re-sync effect clobbers input back
  // to empty (which happens after this child's mount effect, since React
  // runs child effects before parent effects — a value.text-only dep would
  // never observe the clobber because text stays null across renders).
  useEffect(() => {
    if (value.text !== BUDGET_INTRO_ANSWER) {
      onChange({ ...value, text: BUDGET_INTRO_ANSWER });
    }
  }, [value]);

  return (
    <BudgetIntro
      hideBottomBar={true}
      guidanceText={emptyGuidance}
      reportId=""
      onNext={noop}
      onPrevious={noop}
    />
  );
};

const StyledDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACERS.small};
  max-width: 260px;
`;

const placeholderFor = (questionId: number): string => {
  switch (questionId) {
    case DIRECT_COST_QUESTION_ID:
      return 'Total direct cost';
    case IN_KIND_DONATIONS_QUESTION_ID:
      return 'Total in-kind donations';
    case OVERHEAD_COST_QUESTION_ID:
      return 'Total overhead cost';
    default:
      return 'Amount';
  }
};

const relevancyTextFor = (questionId: number): string => {
  switch (questionId) {
    case DIRECT_COST_QUESTION_ID:
      return directCostCheckboxText;
    case IN_KIND_DONATIONS_QUESTION_ID:
      return inKindDonationsCheckboxText;
    case OVERHEAD_COST_QUESTION_ID:
      return overheadCostCheckboxText;
    default:
      return '';
  }
};

interface BudgetFieldProps {
  questionId: number;
  value: AnswerInput;
  onChange: (next: AnswerInput) => void;
  disabled?: boolean;
}

const BudgetField: React.FC<BudgetFieldProps> = ({
  questionId,
  value,
  onChange,
  disabled,
}) => {
  if (questionId === BUDGET_INTRO_QUESTION_ID) {
    return <BudgetIntroField value={value} onChange={onChange} />;
  }

  const currency = getBudgetDollarAmount(value.text ?? undefined);

  const setCurrency = (next: string) => {
    onChange({
      ...value,
      text: next ? JSON.stringify({ currency: next }) : null,
    });
  };

  // RelevancyCheckbox uses a React setState signature; adapt to our setter.
  const setterAdapter: React.Dispatch<React.SetStateAction<
    string
  >> = update => {
    setCurrency(typeof update === 'function' ? update(currency) : update);
  };

  return (
    <StyledDiv>
      <TrueImpactDollarField
        value={currency}
        onChange={setCurrency}
        disabled={disabled || currency === defaultCostValue}
        toFixedDecimal={2}
        placeholder={placeholderFor(questionId)}
      />
      <RelevancyCheckbox
        value={currency}
        handleValue={setterAdapter}
        text={relevancyTextFor(questionId)}
      />
    </StyledDiv>
  );
};

export default BudgetField;
